import type { FC } from 'react';
import { TypeCastingWrapper } from './TypeCasting.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import typecasting1 from './../../../resources/content/typecasting1.png';
import typecasting2 from './../../../resources/content/typecasting2.png';

interface TypeCastingProps { }

const TypeCasting: FC<TypeCastingProps> = () => (
   <TypeCastingWrapper>
      <header className="entry-header ">
         <h1 className="entry-title" itemProp="headline">Type Casting</h1>
      </header>
      <br/>
      <div className="entry-content clear" itemProp="text" style={{ height: 'auto !important' }}>
         <p className="text-align-justify">When the value of one primitive data type is assigned to another type, the process is known as Typecasting.</p>
         <p className="text-align-justify">It is possible that when you assign the value of one primitive data type to another data type, the two types might not be compatible with each other. When the data types are compatible, then Java will perform Automatic Type Conversion. In other words, the conversion will be done automatically. However, if data types are not compatible, then they need to be cast or converted explicitly.</p>
         <p className="text-align-justify"><strong>Types of casting :</strong></p>
         <ul className="text-align-justify">
            <li><strong>Widening Casting</strong>&nbsp; :
               <ul>
                  <li>It converts a smaller type to the larger type size.</li>
                  <li>It happens automatically or implicitly.</li>
                  <li>The two types are compatible.</li>
                  <li>The source type is smaller than the target type.</li>
                  <li>It is also known as Promotion.</li>
               </ul>
               <ul>
                  <li>byte to short,  short to int, int to long, long to float, float to double.<br /></li>
               </ul>
            </li>
         </ul>

         <div>
            <figure className="aligncenter size-large">
               <img decoding="async" width="850" height="152" src={typecasting1} alt="" className="lazy-loaded" sizes="(max-width: 850px) 100vw, 850px" />
               <figcaption><em><span style={{ textDecoration: 'underline' }}>Widening Casting</span></em></figcaption>
            </figure>
         </div>

         <ul className="text-align-justify">
            <li><strong>Narrowing Casting</strong>&nbsp; :
               <ul>
                  <li>It converts a larger type to a smaller size type.</li>
               </ul>
               <ul>
                  <li>It is performed manually or explicitly.</li>
                  <li>The two types are not compatible.</li>
                  <li>The source type is larger than the target type.</li>
                  <li>It is also known as Demotion.</li>
                  <li>double to float, float to long, long to int, int to short, short to byte.</li>
               </ul>
            </li>
         </ul>

         <div>
            <figure className="aligncenter size-large">
               <img decoding="async" width="848" height="134" src={typecasting2} alt="" className="lazy-loaded" sizes="auto, (max-width: 848px) 100vw, 848px" />
               <figcaption><em><span style={{ textDecoration: 'underline' }}>Narrowing Casting</span></em></figcaption>
            </figure>
         </div>

         <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
            {
               '//Automatic or Widening Casting\n' +
               'int i = 100;\n' +
               'long l = i;\n' +
               'float f = l;\n' +
               '\n' +
               '//Manual or Narrowing Casting\n' +
               'double d = 100.04;\n' +
               'long l = (long) d;\n' +
               'int i = (int) l;'
            }
         </SyntaxHighlighter>
      </div>
   </TypeCastingWrapper>
);

export default TypeCasting;
